/* eslint-disable @typescript-eslint/no-unused-vars */
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Warehouse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { TransferOrderPartDetails } from "../../../@types/DRO/GetPartsListResponse";
import { QrCodeItem } from "../../../@types/DRO/SalesOrderResponse";
import { CreateSalesOrderBody } from "../../../@types/DTO/CreateSalesOrderBody";
import { AllPartApi } from "../../../api/GetParts/GetAllPartsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";
import { SalesOrderApi } from "../../../api/OrderManagement/SalesOrderApi";
import { useLoading } from "../../../views/Loader/LoadingContext";
import {
  AppAutoCompleteWithSearchAPI,
  OptionType,
} from "../../Layouts/AppAutoCompleteWithSearchAPI/AppAutoCompleteWithSearchAPI";
import ProductSelectorModal from "../../Layouts/AppModals/ProductSelectorModal";
import VendorAllocation, {
  VendorOption,
  VendorSplitEntry,
} from "../../Layouts/VendorAllocation/VendorAllocation";

export type SalesOrderItem = Omit<TransferOrderPartDetails, "quantity"> & {
  productId: number;
  productName: string;
  quantity: number;
  price: number;
  tax: number;
  availableQuantity?: number;
  isOutOfStock?: boolean;
  _isEmpty?: boolean;
};

type VendorSplitErrors = {
  rows: { [splitId: number]: string };
  section?: string;
};

const VENDOR_ROW_ERROR = "Please select Vendor and fill the quantities";


const getVendorSplitErrors = (
  mainQuantity: number,
  splits: VendorSplitEntry[]
): VendorSplitErrors | null => {
  const rows: VendorSplitErrors["rows"] = {};

  splits.forEach((split) => {
    const qty = Number(split.quantity) || 0;
    const noVendor =
      split.vendorId === "" || split.vendorId === null || split.vendorId === undefined;
    if (noVendor || qty <= 0) {
      rows[split.id] = VENDOR_ROW_ERROR;
    }
  });

  return Object.keys(rows).length > 0 ? { rows } : null;
};


const CreateSalesOrder: React.FC = () => {
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get("status") || "";

  // State for items
  const [items, setItems] = useState<SalesOrderItem[]>([
    {
      id: 0,
      productId: 0,
      productName: "",
      quantity: 0,
      _isEmpty: true,
      price: 0,
      tax: 0,
      externalId: 0,
      referenceCode: "",
      type: "",
      description: "",
      status: 0,
      costPrice: 0,
      averageCostPrice: 0,
      salesPrice: 0,
      penaltyType: "",
      deleted: false,
      updatedBy: 0,
    },
  ]);
  // Always initialize productSearchTexts for each row to empty string
  const [productSearchTexts, setProductSearchTexts] = useState<{
    [row: number]: string;
  }>({ 0: "" });

  // State for customers
  const [customers, setCustomers] = useState<QrCodeItem[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<QrCodeItem | null>(
    null
  );
  const [customerPage, setCustomerPage] = useState(0);
  const [customerHasMore, setCustomerHasMore] = useState(true);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");

  // State for warehouse
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(
    null
  );
  const [warehousePage, setWarehousePage] = useState(0);
  const [warehouseHasMore, setWarehouseHasMore] = useState(true);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [warehouseSearch, setWarehouseSearch] = useState("");

  // State for tracking selected parts
  const [selectedPartIds, setSelectedPartIds] = useState<Set<number>>(
    new Set()
  );
  const [partsRefreshKey, setPartsRefreshKey] = useState(0);

  // Modal state for product selector
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [productModalRowIndex, setProductModalRowIndex] = useState<
    number | null
  >(null);
  const [productModalInitialSearch, setProductModalInitialSearch] =
    useState<string>("");

  // Other states
  const [remarks, setRemarks] = useState<string>("");
  const [creationDate, setCreationDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // ---- Vendor Allocation state ----
  const [vendorAllocationOptions, setVendorAllocationOptions] = useState<
    VendorOption[]
  >([]);
  const [isLoadingVendorOptions, setIsLoadingVendorOptions] = useState(false);
  const [splitActiveRows, setSplitActiveRows] = useState<{
    [key: number]: boolean;
  }>({});
  const [vendorSplits, setVendorSplits] = useState<{
    [key: number]: VendorSplitEntry[];
  }>({});

  const [vendorSplitErrors, setVendorSplitErrors] = useState<{
    [itemIndex: number]: VendorSplitErrors;
  }>({});

  const validateVendorSplits = (): boolean => {
    const errors: typeof vendorSplitErrors = {};
    items.forEach((item, index) => {
      if (!splitActiveRows[index]) return; // only items with vendor allocation enabled
      const result = getVendorSplitErrors(item.quantity, vendorSplits[index] || []);
      if (result) errors[index] = result;
    });
    setVendorSplitErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Clears errors live as the user fixes them (only re-checks rows that currently show an error)
  useEffect(() => {
    setVendorSplitErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      const next: typeof prev = {};
      Object.keys(prev).forEach((key) => {
        const i = Number(key);
        if (!splitActiveRows[i] || !items[i]) return;
        const result = getVendorSplitErrors(items[i].quantity, vendorSplits[i] || []);
        if (result) next[i] = result;
      });
      return next;
    });
  }, [vendorSplits, items, splitActiveRows]);

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleQuantityChange = (index: number, value: string) => {
    const newItems = [...items];
    const availableQty = newItems[index].availableQuantity || 0;
    const partProductType = newItems[index].partProductType;

    if (value === "") {
      // Field is empty - set quantity to 0 and mark as empty
      newItems[index] = {
        ...newItems[index],
        quantity: 0,
        _isEmpty: true,
      };
      setItems(newItems);
      return;
    }

    const inputValue = parseInt(value);

    if (isNaN(inputValue)) {
      return; // Don't update if not a number
    }

    // If SERVICE, allow 0 and skip availableQuantity check
    if (partProductType === "SERVICE") {
      if (inputValue < 0) {
        setSnackbarMessage("Minimum quantity should be 0");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      newItems[index] = {
        ...newItems[index],
        quantity: inputValue,
        _isEmpty: false,
      };
      setItems(newItems);
      return;
    }

    // For all other types, keep original logic
    if (inputValue < 1) {
      setSnackbarMessage("Minimum quantity should be 1");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    if (inputValue > availableQty) {
      setSnackbarMessage(
        `Quantity cannot exceed available stock (${availableQty})`
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    // Valid quantity - update and mark as not empty
    newItems[index] = {
      ...newItems[index],
      quantity: inputValue,
      _isEmpty: false,
    };
    setItems(newItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        id: 0,
        productId: 0,
        productName: "",
        quantity: 0, // Actual value 0
        _isEmpty: true, // But appears empty
        price: 0,
        tax: 18,
        externalId: 0,
        referenceCode: "",
        type: "",
        description: "",
        status: 0,
        costPrice: 0,
        averageCostPrice: 0,
        salesPrice: 0,
        penaltyType: "",
        deleted: false,
        updatedBy: 0,
      },
    ]);
    setProductSearchTexts((prev) => ({ ...prev, [items.length]: "" }));
  };

  // Fetch customers with pagination
  const fetchCustomers = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (customerLoading) return;

      setCustomerLoading(true);
      try {
        const response =
          await SalesOrderApi.api.getAllCustomerListForSalesOrder(
            page,
            20,
            search
          );

        if (append) {
          setCustomers((prev) => [...prev, ...response.data.data]);
        } else {
          setCustomers(response.data.data);
        }

        setCustomerHasMore(page < response.data.totalPages - 1);
        setCustomerPage(page);
      } catch (error) {
        console.error("Error fetching customers", error);
        setSnackbarMessage("Error fetching customers");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setCustomerLoading(false);
      }
    },
    [customerLoading]
  );

  // Fetch warehouses with pagination
  const fetchWarehouses = useCallback(
    async (page: number, search: string, append: boolean = false) => {
      if (warehouseLoading) return;

      setWarehouseLoading(true);
      try {
        const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
          page,
          20,
          search,
          true
        );

        if (append) {
          setWarehouses((prev) => [...prev, ...response.data.data]);
        } else {
          setWarehouses(response.data.data);
        }

        setWarehouseHasMore(page < response.data.totalPages - 1);
        setWarehousePage(page);
      } catch (error) {
        console.error("Error fetching warehouses", error);
        setSnackbarMessage("Error fetching warehouses");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setWarehouseLoading(false);
      }
    },
    [warehouseLoading]
  );

  // Fetch parts with warehouse context
  const fetchPaginatedPartsBySearchTerm = useCallback(
    (data: {
      currentPageNumber: number;
      currentSearchQueryTerm: string;
    }): Promise<{
      options: OptionType<SalesOrderItem>[];
      hasMore: boolean;
    }> => {
      if (!selectedWarehouse) {
        return Promise.resolve({ options: [], hasMore: false });
      }

      return AllPartApi.api
        .getPartList(
          data.currentPageNumber,
          50,
          data.currentSearchQueryTerm,
          undefined,
          undefined,
          undefined,
          selectedWarehouse.id,
          undefined,
          true
        )
        .then(({ data: response }) => {
          // Filter out already selected parts
          const availableParts = response.data.filter(
            (part: any) => !selectedPartIds.has(part.id)
          );

          return {
            options: availableParts.map((part: any) => ({
              value: part.id.toString(),
              label: part.isOutOfStock
                ? `${part.description} (Out of Stock)`
                : part.description,
              data: {
                ...part,
                productId: part.id,
                productName: part.description,
                quantity: 0,
                price: part.salesPrice,
                tax: part.tax,
                availableQuantity: part.quantity,
                isOutOfStock: part.isOutOfStock || false,
              } as SalesOrderItem,
            })),
            hasMore: data.currentPageNumber < response.totalPages - 1,
          };
        })
        .catch(() => ({ options: [], hasMore: false }));
    },
    [selectedWarehouse, selectedPartIds]
  );

  // Fetch vendor allocation options for the selected warehouse
  const fetchVendorOptionsForWarehouse = useCallback(
    async (warehouseId: number) => {
      try {
        setIsLoadingVendorOptions(true);
        const response = await AllPartApi.api.getPartList(
          0,
          50,
          "",
          undefined,
          undefined,
          undefined,
          warehouseId,
          undefined,
          true
        );

        const parts = response?.data?.data || [];
        const vendorMap = new Map<string, VendorOption>();
        parts.forEach((part: any) => {
          (part.vendorAllocations || []).forEach((allocation: any) => {
            if (allocation?.vendorId != null) {
              const key = `${part.id}-${allocation.vendorId}`;
              if (!vendorMap.has(key)) {
                vendorMap.set(key, {
                  productId: part.id,
                  vendorId: allocation.vendorId,
                  vendorName: allocation.vendorName,
                  quantity: allocation.quantity || 0,
                });
              }
            }
          });
        });

        setVendorAllocationOptions(Array.from(vendorMap.values()));
      } catch (error) {
        console.error("Error fetching vendor options", error);
        setVendorAllocationOptions([]);
      } finally {
        setIsLoadingVendorOptions(false);
      }
    },
    []
  );

  // Load initial customers and warehouses
  useEffect(() => {
    fetchCustomers(0, "", false);
    fetchWarehouses(0, "", false);
  }, []);

  // Fetch vendor options whenever selected warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      fetchVendorOptionsForWarehouse(selectedWarehouse.id);
    } else {
      setVendorAllocationOptions([]);
    }
  }, [selectedWarehouse, fetchVendorOptionsForWarehouse]);

  // Reset items when warehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      // Clear all items and selected parts when warehouse changes
      setItems([
        {
          id: 0,
          productId: 0,
          productName: "",
          quantity: 0,
          price: 0,
          tax: 0,
          externalId: 0,
          referenceCode: "",
          type: "",
          description: "",
          status: 0,
          costPrice: 0,
          averageCostPrice: 0,
          salesPrice: 0,
          penaltyType: "",
          deleted: false,
          updatedBy: 0,
        },
      ]);
      setSelectedPartIds(new Set());
      setProductSearchTexts({ 0: "" });
      setSplitActiveRows({});
      setVendorSplits({});
    }
  }, [selectedWarehouse]);

  // Handle product selection
  const handleProductSelect =
    (index: number) =>
      (event: any, value: OptionType<SalesOrderItem> | null) => {
        const newItems = [...items];
        const oldPartId = newItems[index].productId;

        if (value && value.data) {
          // Remove old part ID from selected parts
          if (oldPartId > 0) {
            setSelectedPartIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(oldPartId);
              return newSet;
            });
          }

          // Add new part ID to selected parts
          setSelectedPartIds((prev) => {
            const newSet = new Set(prev);
            newSet.add(value.data.productId);
            return newSet;
          });

          // Update item with new part data
          newItems[index] = {
            ...value.data,
            quantity: 1, // Default quantity
          };
          setItems(newItems);

          // Reset vendor allocation section for this row since product changed
          setSplitActiveRows((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setVendorSplits((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });

          // Force refresh of all dropdowns
          setPartsRefreshKey((prev) => prev + 1);
        } else {
          // Remove old part ID when deselecting
          if (oldPartId > 0) {
            setSelectedPartIds((prev) => {
              const newSet = new Set(prev);
              newSet.delete(oldPartId);
              return newSet;
            });

            // Force refresh of all dropdowns
            setPartsRefreshKey((prev) => prev + 1);
          }

          // Reset the item
          newItems[index] = {
            id: 0,
            productId: 0,
            productName: "",
            quantity: 0,
            price: 0,
            tax: 0,
            externalId: 0,
            referenceCode: "",
            type: "",
            description: "",
            status: 0,
            costPrice: 0,
            averageCostPrice: 0,
            salesPrice: 0,
            penaltyType: "",
            deleted: false,
            updatedBy: 0,
          };
          setItems(newItems);

          // Reset vendor allocation section for this row
          setSplitActiveRows((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
          setVendorSplits((prev) => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
          });
        }
      };

  // Helper to open modal for a row
  const openProductModal = (rowIndex: number) => {
    // Always use the current input value for this row
    const initialSearch = productSearchTexts[rowIndex] || "";
    // Debug log
    // eslint-disable-next-line no-console
    console.log("openProductModal initialSearch:", initialSearch);
    setProductModalRowIndex(rowIndex);
    setProductModalInitialSearch(initialSearch);
    setProductModalOpen(true);
  };
  // Helper to handle selection from modal
  const handleProductModalSelect = (item: SalesOrderItem) => {
    if (productModalRowIndex === null) return;
    const newItems = [...items];
    const oldPartId = newItems[productModalRowIndex].productId;
    // Remove old part ID from selected parts
    if (oldPartId > 0) {
      setSelectedPartIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(oldPartId);
        return newSet;
      });
    }
    // Add new part ID to selected parts
    setSelectedPartIds((prev) => {
      const newSet = new Set(prev);
      newSet.add(item.productId);
      return newSet;
    });
    // Update item with new part data
    newItems[productModalRowIndex] = {
      ...item,
      quantity: 1, // Default quantity
      _isEmpty: false,
    };
    setItems(newItems);

    // Reset vendor allocation section for this row since product changed
    const rowIndex = productModalRowIndex;
    setSplitActiveRows((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });
    setVendorSplits((prev) => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setPartsRefreshKey((prev) => prev + 1);
  };

  // Handle customer search with debounce
  const handleCustomerSearchChange = useCallback(
    (event: any, value: string) => {
      setCustomerSearch(value);
      setCustomerPage(0);
      fetchCustomers(0, value, false);
    },
    [fetchCustomers]
  );

  // Handle warehouse search with debounce
  const handleWarehouseSearchChange = useCallback(
    (event: any, value: string) => {
      setWarehouseSearch(value);
      setWarehousePage(0);
      fetchWarehouses(0, value, false);
    },
    [fetchWarehouses]
  );

  // Handle scroll for customer autocomplete
  const handleCustomerScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight ===
      listboxNode.scrollHeight
    ) {
      if (customerHasMore && !customerLoading) {
        fetchCustomers(customerPage + 1, customerSearch, true);
      }
    }
  };

  // Handle scroll for warehouse autocomplete
  const handleWarehouseScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollTop + listboxNode.clientHeight ===
      listboxNode.scrollHeight
    ) {
      if (warehouseHasMore && !warehouseLoading) {
        fetchWarehouses(warehousePage + 1, warehouseSearch, true);
      }
    }
  };

  // Handle create sales order
  const handleCreateSalesOrder = async () => {
    setLoading(true);

    if (!selectedCustomer || !selectedWarehouse) {
      setSnackbarMessage("Please select customer and warehouse");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    // if (!remarks.trim()) {
    //   setSnackbarMessage("Please enter remarks");
    //   setSnackbarSeverity("error");
    //   setSnackbarOpen(true);
    //   setLoading(false);
    //   return;
    // }

    const validItems = items.filter(
      (item) => item.productId > 0 && !item.isOutOfStock
    );
    if (validItems.length === 0) {
      setSnackbarMessage("Please add at least one available product");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (!validateVendorSplits()) {
      setLoading(false);
      return;
    }

    try {
      const orderBody: CreateSalesOrderBody = {
        products: validItems.map((item) => {
          const itemIndex = items.indexOf(item);
          const splits = vendorSplits[itemIndex];
          return {
            partId: item.productId,
            quantity: item.quantity,
            ...(splits && splits.length > 0
              ? {
                  vendorAllocation: splits
                    .filter((split) => split.vendorId)
                    .map((split) => ({
                      vendorId: Number(split.vendorId),
                      quantity: split.quantity,
                    })),
                }
              : {}),
          };
        }),
        qrCode: selectedCustomer.qrCode,
        warehouseId: selectedWarehouse.id,
        remarks: remarks,
      };

      await SalesOrderApi.api.createSalesOrder(orderBody);

      setSnackbarMessage("Sales order created successfully");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);

      setTimeout(() => {
        navigate(`/sales-order${statusFilter ? `?status=${statusFilter}` : ""}`);
      }, 1000);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Error creating sales order";
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = (index: number) => {
    if (items.length > 1) {
      setVendorSplitErrors({});
      const itemToDelete = items[index];

      // Remove the part ID from selected parts
      if (itemToDelete.productId > 0) {
        setSelectedPartIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(itemToDelete.productId);
          return newSet;
        });

        // Force refresh of all dropdowns
        setPartsRefreshKey((prev) => prev + 1);
      }

      const updatedItems = items.filter((_, i) => i !== index);
      setItems(updatedItems);
      // Remove the search text for the deleted row
      setProductSearchTexts((prev) => {
        const newTexts = { ...prev };
        delete newTexts[index];
        return newTexts;
      });

      // Shift vendor allocation split state indices after deletion
      setSplitActiveRows((prev) => {
        const updated: typeof prev = {};
        Object.entries(prev).forEach(([key, value]) => {
          const rowIndex = parseInt(key);
          if (rowIndex < index) {
            updated[rowIndex] = value;
          } else if (rowIndex > index) {
            updated[rowIndex - 1] = value;
          }
        });
        return updated;
      });

      setVendorSplits((prev) => {
        const updated: typeof prev = {};
        Object.entries(prev).forEach(([key, value]) => {
          const rowIndex = parseInt(key);
          if (rowIndex < index) {
            updated[rowIndex] = value;
          } else if (rowIndex > index) {
            updated[rowIndex - 1] = value;
          }
        });
        return updated;
      });
    }
  };

  // ---- Vendor Allocation handlers ----
  const handleToggleSplitVendor = (index: number) => {
    const isCurrentlyActive = !!splitActiveRows[index];

    if (isCurrentlyActive) {
      // Second click - close/hide the split section
      setSplitActiveRows((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      setVendorSplits((prev) => {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      });
      return;
    }

    // First click - open the split section with a single default vendor row
    setSplitActiveRows((prev) => ({ ...prev, [index]: true }));
    setVendorSplits((prev) => {
      if (prev[index] && prev[index].length >= 1) return prev;
      return {
        ...prev,
        [index]: [
          {
            id: Date.now(),
            vendorId: "",
            vendorName: "",
            quantity: items[index]?.quantity || 0,
            unitPrice: items[index]?.price || 0,
          },
        ],
      };
    });
  };

  const handleAddVendorSplitRow = (index: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      return {
        ...prev,
        [index]: [
          ...existing,
          {
            id: Date.now() + Math.random(),
            vendorId: "",
            vendorName: "",
            quantity: 0,
            unitPrice: items[index]?.price || 0,
          },
        ],
      };
    });
  };

  const handleRemoveVendorSplitRow = (index: number, splitId: number) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      // Keep a minimum of 1 vendor row in the split section
      if (existing.length <= 1) return prev;
      const updated = existing.filter((split) => split.id !== splitId);
      return { ...prev, [index]: updated };
    });
  };

  const handleVendorSplitFieldChange = (
    index: number,
    splitId: number,
    field: "vendorId" | "vendorName" | "quantity",
    value: string | number
  ) => {
    setVendorSplits((prev) => {
      const existing = prev[index] || [];
      const updated = existing.map((split) =>
        split.id === splitId ? { ...split, [field]: value } : split
      );
      return { ...prev, [index]: updated };
    });
  };

  const calculateTotals = () => {
    let totalAmount = 0;
    let totalGST = 0;

    items.forEach((item) => {
      if (!item.isOutOfStock) {
        const quantity = item.quantity || 0;
        const price = item.price || 0;
        const taxRate = item.tax / 100 || 0;

        const itemTotal = quantity * price;
        const itemGST = itemTotal * taxRate;

        totalAmount += itemTotal;
        totalGST += itemGST;
      }
    });

    return {
      totalAmount: totalAmount.toFixed(2),
      totalGST: totalGST.toFixed(2),
      overallTotal: (totalAmount + totalGST).toFixed(2),
    };
  };

  // Vendor options per row: use the selected item's own vendorAllocations first
  // (works for products loaded via scroll/search), fall back to the warehouse-level list.
  const vendorOptionsByItem = useMemo(() => {
    const result: { [index: number]: VendorOption[] } = {};
    items.forEach((item, index) => {
      if (!item.productId || item.productId <= 0) {
        result[index] = [];
        return;
      }

      const own: VendorOption[] = ((item.vendorAllocations as any[]) || [])
        .filter((a) => a?.vendorId != null)
        .map((a) => ({
          productId: item.productId,
          vendorId: a.vendorId,
          vendorName: a.vendorName,
          quantity: a.quantity || 0,
        }));

      result[index] =
        own.length > 0
          ? own
          : vendorAllocationOptions.filter((o) => o.productId === item.productId);
    });
    return result;
  }, [items, vendorAllocationOptions]);

  const { totalAmount, totalGST, overallTotal } = calculateTotals();

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="w-full p-4">
        {/* Header Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}>
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: "600",
                color: "#1E1E1E",
                textAlign: "left",
                fontSize: "1.5rem",
                mb: 0,
              }}>
              {orderId ? "Edit" : "Create"} Sales Order
            </Typography>
          </Box>

          <div className="flex items-center bg-gray-100 px-4 py-2 rounded-md space-x-2">
            <Link
              to="/home"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Dashboard
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <Link
              to={`/sales-order${statusFilter ? `?status=${statusFilter}` : ""}`}
              className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors">
              Orders
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <span className="text-sm font-medium text-gray-700">
              {orderId ? "Edit" : "Create"} Order
            </span>
          </div>
        </Box>

        <Paper
          sx={{
            p: 4,
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "white",
            maxWidth: "1600px",
            margin: "0 auto",
          }}>
          {/* Form Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={3}>
              {/* Customer */}
              <Grid item xs={12} md={4}>
                <Box>
                  <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                    Customer (QR Code )
                  </label>
                  <Autocomplete
                    fullWidth
                    options={customers}
                    getOptionLabel={(option) => option.qrCode || ""}
                    isOptionEqualToValue={(option, value) =>
                      !!option && !!value && option.qrCode === value.qrCode
                    }
                    value={selectedCustomer}
                    onChange={(_, newValue) => {
                      setSelectedCustomer(newValue);
                    }}
                    onInputChange={handleCustomerSearchChange}
                    loading={customerLoading}
                    ListboxProps={{
                      onScroll: handleCustomerScroll,
                      style: { maxHeight: 250 },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        placeholder="Select customer QR code"
                        InputProps={{
                          ...params.InputProps,
                          style: { fontSize: "0.875rem" },
                          endAdornment: (
                            <>
                              {customerLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
                {/* Customer */}
                <Grid item xs={12} md={4}>
                  <Box>
                    <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                      Cycle No
                    </label>
                    <Autocomplete
                      fullWidth
                      options={customers}
                        getOptionLabel={(option) => option.cycleNo || ""}
                        isOptionEqualToValue={(option, value) =>
                          !!option && !!value && option.cycleNo === value.cycleNo
                        }
                      value={selectedCustomer}
                      onChange={(_, newValue) => {
                        setSelectedCustomer(newValue);
                      }}
                      onInputChange={handleCustomerSearchChange}
                      loading={customerLoading}
                      ListboxProps={{
                        onScroll: handleCustomerScroll,
                        style: { maxHeight: 250 },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select customer cycle no"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                            endAdornment: (
                              <>
                                {customerLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Warehouse */}
                <Grid item xs={12} md={4}>
                  <Box>
                    <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                      Warehouse
                    </label>
                    <Autocomplete
                      fullWidth
                      options={warehouses}
                      getOptionLabel={(option) => option.name}
                      value={selectedWarehouse}
                      onChange={(_, newValue) => {
                        setSelectedWarehouse(newValue);
                      }}
                      onInputChange={handleWarehouseSearchChange}
                      loading={warehouseLoading}
                      ListboxProps={{
                        onScroll: handleWarehouseScroll,
                        style: { maxHeight: 250 },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select warehouse"
                          InputProps={{
                            ...params.InputProps,
                            style: { fontSize: "0.875rem" },
                            endAdornment: (
                              <>
                                {warehouseLoading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                {/* Creation Date */}
                <Grid item xs={12} md={4}>
                  <Box>
                    <label className="block text-gray-600 text-sm font-medium text-start mb-1">
                      Creation Date
                    </label>
                    <TextField
                      fullWidth
                      type="date"
                      value={creationDate}
                      onChange={(e) => setCreationDate(e.target.value)}
                      variant="standard"
                      InputProps={{
                        style: { fontSize: "0.875rem" },
                      }}
                    />
                  </Box>
                </Grid>
            </Grid>
          </Box>

          {/* Items Section */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="subtitle1"
              sx={{
                textAlign: "left",
                fontWeight: "600",
                color: "#1E1E1E",
                mb: 2,
                fontSize: "1.125rem",
              }}>
              Products
            </Typography>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1200 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      #
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        width: "30%",
                      }}>
                      Product
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Reference Code
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Available Qty
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Order Quantity
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Unit Price (₹)
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Tax (%)
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Total (₹)
                    </TableCell>
                    <TableCell sx={{ fontWeight: "600", fontSize: "0.875rem" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const isOutOfStock = item.isOutOfStock || false;
                    const availableQty = item.availableQuantity || 0;
                    const isQuantityEntered =
                      !item._isEmpty && item.quantity > 0;

                    return (
                      <React.Fragment key={index}>
                        <TableRow
                          sx={{
                            backgroundColor: isOutOfStock
                              ? "#f5f5f5"
                              : "inherit",
                            opacity: isOutOfStock ? 0.6 : 1,
                          }}>
                          <TableCell sx={{ fontSize: "0.875rem" }}>
                            {index + 1}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <Box sx={{ flex: 1 }}>
                                <AppAutoCompleteWithSearchAPI<SalesOrderItem>
                                  key={`part-select-${index}-warehouse-${selectedWarehouse?.id || "none"
                                    }-refresh-${partsRefreshKey}`}
                                  initialPageNumber={0}
                                  value={
                                    item.productId > 0
                                      ? {
                                        value: item.productId.toString(),
                                        label: item.isOutOfStock
                                          ? `${item.productName} (Out of Stock)`
                                          : item.productName,
                                        data: item,
                                      }
                                      : null
                                  }
                                  inputValue={productSearchTexts[index] || ""}
                                  onChange={handleProductSelect(index)}
                                  placeholder={
                                    !selectedWarehouse
                                      ? "Select warehouse first"
                                      : "Select product"
                                  }
                                  disabled={!selectedWarehouse}
                                  getData={fetchPaginatedPartsBySearchTerm}
                                  onInputChange={(_event, newInputValue) => {
                                    setProductSearchTexts((prev) => ({
                                      ...prev,
                                      [index]: newInputValue,
                                    }));
                                  }}
                                />
                              </Box>
                              {/* Show All button for large result set */}
                              <Button
                                size="small"
                                sx={{ ml: 1, minWidth: 0, p: 0.5 }}
                                onClick={() => openProductModal(index)}
                                disabled={!selectedWarehouse}
                                title="Show All Products">
                                Show All
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.referenceCode || ""}
                              variant="standard"
                              disabled
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={availableQty}
                              variant="standard"
                              disabled
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <TextField
                                value={
                                  item._isEmpty ? "" : item.quantity.toString()
                                }
                                onChange={(e) =>
                                  handleQuantityChange(index, e.target.value)
                                }
                                variant="standard"
                                disabled={!item.productName || isOutOfStock}
                                InputProps={{
                                  style: { fontSize: "0.875rem" },
                                  inputProps: { min: 1, max: availableQty },
                                }}
                              />
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={!isQuantityEntered || isOutOfStock}
                                onClick={() => handleToggleSplitVendor(index)}
                                sx={{
                                  width: "120px",
                                  flex: "none",  
                                  textTransform: "none",
                                  borderRadius: "20px",
                                  fontSize: "0.75rem",
                                  fontWeight: 500,
                                  borderColor: splitActiveRows[index]
                                    ? "#1366D9"
                                    : "#D0D5DD",
                                  color: splitActiveRows[index]
                                    ? "#1366D9"
                                    : "#344054",
                                  backgroundColor: splitActiveRows[index]
                                    ? "#EEF4FE"
                                    : "transparent",
                                  whiteSpace: "nowrap",
                                  "&:hover": {
                                    borderColor: "#1366D9",
                                    backgroundColor: splitActiveRows[index]
                                      ? "#EEF4FE"
                                      : "transparent",
                                  },
                                }}>
                                {splitActiveRows[index]
                                  ? "Selecting Vendor"
                                  : "Select Vendor"}
                              </Button>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              value={item.price || ""}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].price =
                                  parseFloat(e.target.value) || 0;
                                setItems(newItems);
                              }}
                              variant="standard"
                              disabled
                              // disabled={!item.productName || isOutOfStock}
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={item.tax || ""}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[index].tax =
                                  parseFloat(e.target.value) || 0;
                                setItems(newItems);
                              }}
                              variant="standard"
                              // disabled={isOutOfStock}
                              disabled
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              value={
                                isOutOfStock
                                  ? "N/A"
                                  : (
                                    item.quantity *
                                    item.price *
                                    (1 + item.tax / 100)
                                  ).toFixed(2)
                              }
                              variant="standard"
                              disabled
                              InputProps={{
                                style: { fontSize: "0.875rem" },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}>
                              <IconButton
                                onClick={() => handleDeleteItem(index)}
                                disabled={items.length === 1}
                                sx={{
                                  color:
                                    items.length === 1 ? "#ccc" : "#FF0000",
                                }}
                                size="small">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>

                        {/* Vendor Allocation expandable section */}
                        {splitActiveRows[index] && (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              sx={{
                                backgroundColor: "#FFFFFF",
                                padding: 1,
                                border: "none",
                              }}>
                              <VendorAllocation
                                index={index}
                                productId={item.productId}
                                quantity={item.quantity}
                                price={item.price}
                                tax={item.tax}
                                vendorSplits={vendorSplits[index] || []}
                                vendorAllocationOptions={vendorOptionsByItem[index] || []}
                                isLoadingVendorOptions={isLoadingVendorOptions}
                                onAddVendorSplitRow={handleAddVendorSplitRow}
                                onRemoveVendorSplitRow={handleRemoveVendorSplitRow}
                                onVendorSplitFieldChange={handleVendorSplitFieldChange}
                                onCancel={handleToggleSplitVendor}
                              />
                              {vendorSplitErrors[index] && (
                                <Box sx={{pb: 1 , mt:0.5 }}>
                                  {Object.entries(vendorSplitErrors[index].rows).map(([splitId, message]) => {
                                    const rowNumber =
                                      (vendorSplits[index] || []).findIndex((s) => String(s.id) === splitId) + 1;
                                    return (
                                      <Typography
                                        key={splitId}
                                        variant="caption"
                                        sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold' }}>
                                        Vendor {rowNumber}: {message}
                                      </Typography>
                                    );
                                  })}
                                  {vendorSplitErrors[index].section && (
                                    <Typography
                                      variant="caption"
                                      sx={{ display: "block", color: "error.main", fontSize: "0.9rem", fontWeight: 'bold'}}>
                                      {vendorSplitErrors[index].section}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
              <IconButton
                onClick={handleAddItem}
                disabled={!selectedWarehouse}
                sx={{
                  color: !selectedWarehouse ? "#ccc" : "#1976D2",
                  mr: 1,
                }}
                size="small">
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="body1"
                sx={{
                  cursor: !selectedWarehouse ? "not-allowed" : "pointer",
                  color: !selectedWarehouse ? "#ccc" : "#1976D2",
                  fontSize: "0.875rem",
                }}
                onClick={!selectedWarehouse ? undefined : handleAddItem}>
                Add Product
              </Typography>
              {!selectedWarehouse && (
                <Typography
                  variant="caption"
                  sx={{
                    ml: 2,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                  }}>
                  (Select warehouse first)
                </Typography>
              )}
            </Box>
          </Box>

          {/* Remarks Section */}
          <Box sx={{ mb: 4 }}>
            <label className="block text-gray-600 text-sm font-medium text-start mb-1">
              Remarks
            </label>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter remarks"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </Box>

          {/* Totals and Actions Section */}
          <Box sx={{ mt: 3 }}>
            <Box sx={{ mb: 2, textAlign: "right" }}>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                Subtotal - ₹ {totalAmount}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "#646464", fontSize: "0.875rem" }}>
                Total Tax - ₹ {totalGST}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#1E1E1E",
                  fontWeight: "600",
                  fontSize: "1rem",
                  mt: 1,
                }}>
                Grand Total - ₹ {overallTotal}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => navigate(`/sales-order${statusFilter ? `?status=${statusFilter}` : ""}`)}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  backgroundColor: "#F94932",
                  "&:hover": { backgroundColor: "#e03e2a" },
                }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleCreateSalesOrder}
                sx={{
                  padding: "8px 20px",
                  borderRadius: "32px",
                  backgroundColor: "#1E1E1E",
                  textTransform: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  "&:hover": { backgroundColor: "#000000" },
                }}>
                Create Order
              </Button>
            </Box>
          </Box>

          {/* Snackbar for notifications */}
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbarSeverity}
              sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </Paper>
      </div>
      {/* Product Selector Modal */}
      <ProductSelectorModal
        open={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        onSelect={handleProductModalSelect}
        warehouseId={selectedWarehouse?.id || null}
        selectedPartIds={selectedPartIds}
        initialSearch={productModalInitialSearch}
      />
    </div>
  );
};

export default CreateSalesOrder;
